import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ButtonComponent, DropdownHeaderDirective, DropdownFooterDirective } from './button/button.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { AlertComponent } from './alert/alert.component';
import { RadioButtonComponent } from './radio-button/radio-button.component';
import { ListComponent } from './list/list.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { PopoverComponent } from './popover/popover.component';
import { TextboxComponent } from './textbox/textbox.component';
import { TextareaComponent } from './textarea/textarea.component';
import { BadgeComponent } from './badge/badge.component';
import { SegmentedToggleComponent } from './segmented-toggle/segmented-toggle.component';
import { ModalComponent } from './modal/modal.component';
import { DataTableComponent } from './data-table/data-table.component';
import { ToggleSwitcherComponent } from './toggle-switcher/toggle-switcher.component';
import { AvatarComponent } from './avatar/avatar.component';
import { SelectableCardComponent } from './selectable-card/selectable-card.component';
import { StepperComponent } from './stepper/stepper.component';
import { PopoverListSelectorComponent } from './popover-list-selector/popover-list-selector.component';
import { NotificationComponent } from './notification/notification.component';
import { NotificationListItemComponent } from './notification-list-item/notification-list-item.component';
import { NotificationListComponent } from './notification-list/notification-list.component';
import { DateSelectorComponent } from './date-selector/date-selector.component';
import { AccordionComponent } from './accordion/accordion.component';
import { AccordionPanelComponent, AccordionHeaderDirective } from './accordion/accordion-panel.component';
import { QuickUploadComponent } from './quick-upload/quick-upload.component';
import { PaginationComponent } from './pagination/pagination.component';
import { MainNavigationComponent } from './main-navigation/main-navigation.component';
import { TopBarComponent } from './top-bar/top-bar.component';
import { TopBarShowcaseComponent } from './showcase/top-bar-showcase/top-bar-showcase.component';
import { AccordionShowcaseComponent } from './showcase/accordion-showcase/accordion-showcase.component';
import { UiShowcaseComponent } from './showcase/ui-showcase.component';
import { ButtonShowcaseComponent } from './showcase/button-showcase/button-showcase.component';
import { NotificationShowcaseComponent } from './showcase/notification-showcase/notification-showcase.component';
import { NotificationListItemShowcaseComponent } from './showcase/notification-list-item-showcase/notification-list-item-showcase.component';
import { NotificationListShowcaseComponent } from './showcase/notification-list-showcase/notification-list-showcase.component';
import { DropdownShowcaseComponent } from './showcase/dropdown-showcase/dropdown-showcase.component';
import { AlertShowcaseComponent } from './showcase/alert-showcase/alert-showcase.component';
import { RadioButtonShowcaseComponent } from './showcase/radio-button-showcase/radio-button-showcase.component';
import { ListShowcaseComponent } from './showcase/list-showcase/list-showcase.component';
import { CheckboxShowcaseComponent } from './showcase/checkbox-showcase/checkbox-showcase.component';
import { PopoverShowcaseComponent } from './showcase/popover-showcase/popover-showcase.component';
import { TextboxShowcaseComponent } from './showcase/textbox-showcase/textbox-showcase.component';
import { TextareaShowcaseComponent } from './showcase/textarea-showcase/textarea-showcase.component';
import { BadgeShowcaseComponent } from './showcase/badge-showcase/badge-showcase.component';
import { SegmentedToggleShowcaseComponent } from './showcase/segmented-toggle-showcase/segmented-toggle-showcase.component';
import { ModalShowcaseComponent } from './showcase/modal-showcase/modal-showcase.component';
import { DataTableShowcaseComponent } from './showcase/data-table-showcase/data-table-showcase.component';
import { ToggleSwitcherShowcaseComponent } from './showcase/toggle-switcher-showcase/toggle-switcher-showcase.component';
import { AvatarShowcaseComponent } from './showcase/avatar-showcase/avatar-showcase.component';
import { SelectableCardShowcaseComponent } from './showcase/selectable-card-showcase/selectable-card-showcase.component';
import { StepperShowcaseComponent } from './showcase/stepper-showcase/stepper-showcase.component';
import { PopoverListSelectorShowcaseComponent } from './showcase/popover-list-selector-showcase/popover-list-selector-showcase.component';
import { DateSelectorShowcaseComponent } from './showcase/date-selector-showcase/date-selector-showcase.component';
import { QuickUploadShowcaseComponent } from './showcase/quick-upload-showcase/quick-upload-showcase.component';
import { PaginationShowcaseComponent } from './showcase/pagination-showcase/pagination-showcase.component';
import { MainNavigationShowcaseComponent } from './showcase/main-navigation-showcase/main-navigation-showcase.component';
import { BrandIconComponent } from './brand-icon/brand-icon.component';
import { BrandIconShowcaseComponent } from './showcase/brand-icon-showcase/brand-icon-showcase.component';
import { BannerSliderComponent } from './banner-slider/banner-slider.component';
import { BannerSliderShowcaseComponent } from './showcase/banner-slider-showcase/banner-slider-showcase.component';
import { PageBannerComponent } from './page-banner/page-banner.component';
import { PageBannerShowcaseComponent } from './showcase/page-banner-showcase/page-banner-showcase.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule
  ],
  declarations: [
    ButtonComponent,
    DropdownHeaderDirective,
    DropdownFooterDirective,
    DropdownComponent,
    AlertComponent,
    RadioButtonComponent,
    ListComponent,
    CheckboxComponent,
    PopoverComponent,
    TextboxComponent,
    TextareaComponent,
    BadgeComponent,
    SegmentedToggleComponent,
    ModalComponent,
    DataTableComponent,
    ToggleSwitcherComponent,
    AvatarComponent,
    SelectableCardComponent,
    StepperComponent,
    PopoverListSelectorComponent,
    NotificationComponent,
    NotificationListItemComponent,
    NotificationListComponent,
    DateSelectorComponent,
    QuickUploadComponent,
    PaginationComponent,
    MainNavigationComponent,
    AccordionComponent,
    AccordionPanelComponent,
    AccordionHeaderDirective,
    AccordionShowcaseComponent,
    PaginationShowcaseComponent,
    UiShowcaseComponent,
    ButtonShowcaseComponent,
    NotificationShowcaseComponent,
    NotificationListItemShowcaseComponent,
    NotificationListShowcaseComponent,
    DropdownShowcaseComponent,
    AlertShowcaseComponent,
    RadioButtonShowcaseComponent,
    ListShowcaseComponent,
    CheckboxShowcaseComponent,
    PopoverShowcaseComponent,
    TextboxShowcaseComponent,
    TextareaShowcaseComponent,
    BadgeShowcaseComponent,
    SegmentedToggleShowcaseComponent,
    ModalShowcaseComponent,
    DataTableShowcaseComponent,
    ToggleSwitcherShowcaseComponent,
    AvatarShowcaseComponent,
    SelectableCardShowcaseComponent,
    StepperShowcaseComponent,
    PopoverListSelectorShowcaseComponent,
    DateSelectorShowcaseComponent,
    QuickUploadShowcaseComponent,
    MainNavigationShowcaseComponent,
    TopBarComponent,
    TopBarShowcaseComponent,
    BrandIconComponent,
    BrandIconShowcaseComponent,
    BannerSliderComponent,
    BannerSliderShowcaseComponent,
    PageBannerComponent,
    PageBannerShowcaseComponent
  ],
  exports: [
    ButtonComponent,
    DropdownHeaderDirective,
    DropdownFooterDirective,
    DropdownComponent,
    AlertComponent,
    RadioButtonComponent,
    ListComponent,
    CheckboxComponent,
    PopoverComponent,
    TextboxComponent,
    TextareaComponent,
    BadgeComponent,
    SegmentedToggleComponent,
    ModalComponent,
    DataTableComponent,
    ToggleSwitcherComponent,
    AvatarComponent,
    SelectableCardComponent,
    StepperComponent,
    PopoverListSelectorComponent,
    NotificationComponent,
    NotificationListItemComponent,
    NotificationListComponent,
    DateSelectorComponent,
    QuickUploadComponent,
    PaginationComponent,
    MainNavigationComponent,
    AccordionComponent,
    AccordionPanelComponent,
    AccordionHeaderDirective,
    AccordionShowcaseComponent,
    PaginationShowcaseComponent,
    UiShowcaseComponent,
    ButtonShowcaseComponent,
    NotificationShowcaseComponent,
    NotificationListItemShowcaseComponent,
    NotificationListShowcaseComponent,
    DropdownShowcaseComponent,
    AlertShowcaseComponent,
    RadioButtonShowcaseComponent,
    ListShowcaseComponent,
    CheckboxShowcaseComponent,
    PopoverShowcaseComponent,
    TextboxShowcaseComponent,
    TextareaShowcaseComponent,
    BadgeShowcaseComponent,
    SegmentedToggleShowcaseComponent,
    ModalShowcaseComponent,
    DataTableShowcaseComponent,
    ToggleSwitcherShowcaseComponent,
    AvatarShowcaseComponent,
    SelectableCardShowcaseComponent,
    StepperShowcaseComponent,
    PopoverListSelectorShowcaseComponent,
    DateSelectorShowcaseComponent,
    QuickUploadShowcaseComponent,
    MainNavigationShowcaseComponent,
    TopBarComponent,
    TopBarShowcaseComponent,
    BrandIconComponent,
    BrandIconShowcaseComponent,
    BannerSliderComponent,
    BannerSliderShowcaseComponent,
    PageBannerComponent,
    PageBannerShowcaseComponent
  ]
})
export class UiModule { }
